import { useView } from '../hooks';

import {
  PractitionerLocationDetails,
  SearchHeader,
  ViewToggle,
} from '~/components';

import { useAppSelector } from '~/state';
import { useAppDispatch } from '~/state/hooks';
import {
  searchPractitionersByLocation,
  selectSearchResults,
} from '~/state/search';
import { selectLocation } from '~/state/location';
import { selectDiscipline } from '~/state/discipline';
import { useEffect, ReactElement } from 'react';
import { selectSelectedPractitioner } from '~/state/activeSelection';
import { SearchResultList, MapContainer } from '~/views';
import { selectAvailability } from '~/state/searchFilters';
import { trackEvent, trackPageView, useSyncReduxToUrlParams } from '~/hooks';
import { Views } from '~/utilities';
import cn from 'classnames';
import { hasFeatureFlag } from '@janeapp/feature-flags-client';
import noPractitionersFoundIcon from '~/assets/images/no-practitioners-found.png';
interface SearchProps {
  locationInput: ReactElement;
}

export const Search = ({ locationInput }: SearchProps) => {
  const { view, handleViewChange } = useView();
  const dispatch = useAppDispatch();
  useSyncReduxToUrlParams();
  const discipline = useAppSelector(selectDiscipline);
  const location = useAppSelector(selectLocation);
  const selectedPractitioner = useAppSelector(selectSelectedPractitioner);
  const results = useAppSelector(selectSearchResults) || [];
  const availability = useAppSelector(selectAvailability);
  const searchResultsReturned = Boolean(results.length);
  const getGridContainerClass = () => {
    if (selectedPractitioner) return 'grid-container has-details';
    if (searchResultsReturned) return 'grid-container';
    return 'grid-container-no-toggle';
  };

  useEffect(() => {
    trackPageView('mpl_search_page_viewed', { path: window.location.pathname });
  }, []);

  // Reset scroll position when navigating to search page
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  //if there is a discipline and location, trigger search for clinics
  useEffect(() => {
    if (discipline && location) {
      dispatch(searchPractitionersByLocation());
      trackEvent('mpl_new_search_started', {
        url: window.location.pathname,
        discipline: discipline,
        location: location.value,
      });
    }
  }, [discipline, location, dispatch, availability]);

  const listContainerClasses = cn(
    'grid-area-list flex-col overflow-hidden md:border-r md:border-neutral-30',
    view !== Views.Map && !selectedPractitioner ? 'flex' : 'hidden'
  );

  const getMapContainer = () => {
    if (hasFeatureFlag('NORTH_VANCOUVER_BETA')) {
      return discipline && location ? (
        <>
          <MapContainer view={view} results={results} />
          <ViewToggle currentView={view} onToggle={handleViewChange} />
        </>
      ) : (
        <div className="grid-area-map flex h-full w-full items-center justify-center">
          <img
            src={noPractitionersFoundIcon}
            alt="Illustration showing no search results found"
            className="h-40 w-40"
          />
        </div>
      );
    }

    return (
      <>
        <MapContainer view={view} results={results} />
        {searchResultsReturned && (
          <ViewToggle currentView={view} onToggle={handleViewChange} />
        )}
      </>
    );
  };

  return (
    <div
      data-testid="search-page"
      className="mobile-safe-area-insets h-dvh w-dvw"
    >
      <div
        data-testid="grid-container"
        className={`${getGridContainerClass()} h-full w-full`}
      >
        {selectedPractitioner && (
          <PractitionerLocationDetails result={selectedPractitioner} />
        )}
        <SearchHeader
          discipline={discipline}
          location={location}
          locationInput={locationInput}
        />
        <div data-testid="search-result-list" className={listContainerClasses}>
          <SearchResultList results={results} />
        </div>
        {getMapContainer()}
      </div>
    </div>
  );
};
